<template>
  <section class="grid-records-panel" aria-label="Active grids">
    <header class="records-header">
      <div>
        <p class="records-kicker">Live inventory</p>
        <h2>Active Grids Overview</h2>
      </div>

      <div class="records-actions">
        <span class="records-count">
          <strong>{{ gridArchives.length }}</strong>
          active grid{{ gridArchives.length === 1 ? "" : "s" }}
        </span>

        <button
          v-if="appStore.state.activeUser?.role == 'lab-admin'"
          type="button"
          class="export-command"
          :disabled="!canExportGridData"
          :title="exportCsvTitle"
          @click="downloadGridData"
        >
          <span class="material-symbols-outlined" aria-hidden="true">download</span>
          Export CSV
        </button>
      </div>
    </header>

    <div v-if="!inventoryStore.isInitialized" class="records-state">
      <span class="material-symbols-outlined" aria-hidden="true">sync</span>
      <strong>Loading inventory data...</strong>
    </div>

    <div v-else-if="gridArchives.length == 0" class="records-state">
      <span class="material-symbols-outlined" aria-hidden="true">grid_off</span>
      <strong>No active grids found.</strong>
    </div>

    <div v-else class="records-table-shell">
      <table class="records-table">
        <thead>
          <tr>
            <th>
              <button type="button" class="sort-command" @click="sortByName()">
                Grid Name
                <span class="material-symbols-outlined" aria-hidden="true">{{ sortIcon("name") }}</span>
              </button>
            </th>
            <th>Description</th>
            <th>Image</th>
            <th>Status</th>
            <th>Catalog Type</th>
            <th>Tomo</th>
            <th>
              <button type="button" class="sort-command" @click="sortByDate()">
                Date Created
                <span class="material-symbols-outlined" aria-hidden="true">{{ sortIcon("date") }}</span>
              </button>
            </th>
            <th>
              <button type="button" class="sort-command" @click="sortByBox()">
                Box Project
                <span class="material-symbols-outlined" aria-hidden="true">{{ sortIcon("box") }}</span>
              </button>
            </th>
            <th>Navigate</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="grid in gridArchives" :key="canonicalId(grid)">
            <td class="grid-name-cell">
              <strong>{{ grid.name || "Untitled grid" }}</strong>
            </td>
            <td class="description-cell">
              <span :title="grid.description">{{ grid.description || "No description" }}</span>
            </td>
            <td>
              <a
                v-if="grid.imageurl"
                :href="getFullImageUrl(grid.imageurl)"
                target="_blank"
                class="table-link"
              >
                View Image
              </a>
              <span v-else class="muted-cell">None</span>
            </td>
            <td>
              <div class="status-stack">
                <span
                  v-for="status in statusLabels(grid.gridType)"
                  :key="status"
                  class="status-pill"
                >
                  {{ status }}
                </span>
                <span v-if="statusLabels(grid.gridType).length === 0" class="muted-cell">
                  Not screened
                </span>
              </div>
            </td>
            <td>{{ grid.gridCatalogType || "" }}</td>
            <td>
              <span v-if="grid.forTomography" class="tomo-pill">CryoET</span>
            </td>
            <td>{{ cleanDate(grid.displayDateCreated, true, true) }}</td>
            <td>{{ grid.boxProject }}</td>
            <td>
              <button type="button" class="go-command" @click="goToGrid(grid)">
                <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                Go
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { useInventoryStore } from "@/stores/inventory";

export default {
  props: ["noimport"],
  data() {
    const inventoryStore = useInventoryStore();
    return {
      inventoryStore,
      currentSort: "ascdate",
    };
  },
  computed: {
    gridArchives() {
      if (!this.inventoryStore.isInitialized) return [];

      const rows = this.inventoryStore.gridsList
        .filter((grid) => !grid.archived)
        .map((grid) => {
          const boxId = grid.boxId || grid.box;
          const box = boxId ? this.inventoryStore.getBox(boxId) : null;
          const puckId = box?.puckId || box?.puck;
          const puck = puckId ? this.inventoryStore.getPuck(puckId) : null;
          const projectId = box?.projectId || box?.project;
          const project = projectId
            ? this.appStore.state.projects.find((entry) => this.canonicalId(entry) === this.canonicalId(projectId))
            : null;

          return {
            ...grid,
            displayDateCreated: grid.trueDateCreated || grid.dateCreated || "",
            boxProject: project?.name || "",
            boxPuckSerialNo: puck?.serialNo || "",
            boxWellNo: box?.wellNo ?? null,
          };
        });

      const sort = this.currentSort;
      if (sort === "ascname") rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      else if (sort === "dscname") rows.sort((a, b) => String(b.name || "").localeCompare(String(a.name || "")));
      else if (sort === "ascdate") rows.sort((a, b) => String(a.displayDateCreated || "").localeCompare(String(b.displayDateCreated || "")));
      else if (sort === "dscdate") rows.sort((a, b) => String(b.displayDateCreated || "").localeCompare(String(a.displayDateCreated || "")));
      else if (sort === "ascbox") rows.sort((a, b) => String(a.boxProject || "").localeCompare(String(b.boxProject || "")));
      else if (sort === "dscbox") rows.sort((a, b) => String(b.boxProject || "").localeCompare(String(a.boxProject || "")));

      return rows;
    },
    canExportGridData() {
      return this.appStore.state.activeUser?.role == "lab-admin" && this.gridArchives.length > 0;
    },
    exportCsvTitle() {
      if (!this.inventoryStore.isInitialized) return "Inventory data is still loading.";
      if (this.gridArchives.length === 0) return "No active grids to export.";
      return "Export active grids as CSV.";
    },
  },
  methods: {
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    cleanDate(date, shortmonth, notime) {
      return this.appStore.state.cleanDate(date, shortmonth, notime);
    },
    goToGrid(grid) {
      const specialNavigation = {
        gridId: this.canonicalId(grid),
        puck: grid.boxPuckSerialNo,
        well: grid.boxWellNo,
        gridPosition: grid.position,
      };
      this.$router.push({
        path: "/manage-products",
        query: { navigation: JSON.stringify(specialNavigation) },
      });
    },
    sortByName() {
      this.currentSort = this.currentSort == "ascname" ? "dscname" : "ascname";
    },
    sortByDate() {
      this.currentSort = this.currentSort == "ascdate" ? "dscdate" : "ascdate";
    },
    sortByBox() {
      this.currentSort = this.currentSort == "ascbox" ? "dscbox" : "ascbox";
    },
    sortIcon(column) {
      if (!this.currentSort.includes(column)) return "unfold_more";
      return this.currentSort.startsWith("asc") ? "keyboard_arrow_up" : "keyboard_arrow_down";
    },
    getFullImageUrl(imageUrl) {
      if (!imageUrl) return "";
      if (imageUrl.startsWith("http")) return imageUrl;
      return `${this.appStore.state.hostname}${imageUrl}`;
    },
    statusLabels(gridType) {
      if (Array.isArray(gridType) && typeof gridType !== "string") {
        return [
          gridType[0] && gridType[0] != "Not Screened" ? "Screened" : "",
          gridType[1] ? "Imaged" : "",
          gridType[2] ? "Tossed" : "",
        ].filter(Boolean);
      }
      return gridType == "Screened" ? [gridType] : [];
    },
    formatGridType(gridType) {
      return this.statusLabels(gridType).join(", ");
    },
    escapeCsv(value) {
      const text = value == null ? "" : String(value);
      if (!/[",\n]/.test(text)) return text;
      return `"${text.replace(/"/g, '""')}"`;
    },
    downloadGridData() {
      if (!this.canExportGridData) return;
      const rows = [
        ["Grid Name", "Description", "Grid Type", "Catalog Type", "Tomo", "Date Created", "Box Project"],
        ...this.gridArchives.map((grid) => [
          grid.name,
          grid.description,
          this.formatGridType(grid.gridType),
          grid.gridCatalogType || "",
          grid.forTomography ? "CryoET" : "",
          this.cleanDate(grid.displayDateCreated, true, true),
          grid.boxProject,
        ]),
      ];
      const csv = rows.map((row) => row.map((cell) => this.escapeCsv(cell)).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "active-grid-export.csv";
      link.click();
      URL.revokeObjectURL(url);
    },
  },
  mounted() {
    if (this.appStore.state.activeUser?.role == "lab-admin") {
      this.appStore.dispatch("getAdminStuff", { url: "projects" }).catch((err) => {
        console.error("Failed to load projects for active grid export:", err);
      });
    }
  },
};
</script>

<style scoped>
.grid-records-panel {
  overflow: hidden;
  border: 1px solid #d9e2ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 24px rgb(15 23 42 / 0.06);
}

.records-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #d9e2ec;
  background: #fbfdff;
}

.records-kicker {
  margin: 0;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.records-header h2 {
  margin: 0.1rem 0 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 900;
}

.records-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.records-count {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  min-height: 34px;
  padding: 0 0.7rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  color: #526176;
  font-size: 0.82rem;
  font-weight: 800;
}

.records-count strong {
  color: #0b5b70;
  font-size: 0.95rem;
}

.export-command,
.go-command {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 34px;
  border: 0;
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: 900;
  cursor: pointer;
}

.export-command {
  padding: 0 0.8rem;
  background: #0b5b70;
  color: #fff;
}

.export-command:disabled {
  background: #d6dee7;
  color: #7a8796;
  cursor: not-allowed;
}

.export-command .material-symbols-outlined,
.go-command .material-symbols-outlined {
  font-size: 18px;
}

.records-state {
  display: grid;
  min-height: 158px;
  padding: 1.25rem;
  place-items: center;
  align-content: center;
  gap: 0.5rem;
  color: #64748b;
  text-align: center;
}

.records-state .material-symbols-outlined {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 999px;
  background: #e0f2f1;
  color: #0b5b70;
  font-size: 24px;
}

.records-state strong {
  color: #334155;
  font-size: 0.95rem;
}

.records-table-shell {
  overflow-x: auto;
}

.records-table {
  width: 100%;
  min-width: 1040px;
  border-collapse: separate;
  border-spacing: 0;
}

.records-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 0.72rem 0.85rem;
  border-bottom: 1px solid #d9e2ec;
  background: #eef3f7;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.records-table td {
  max-width: 260px;
  padding: 0.78rem 0.85rem;
  border-bottom: 1px solid #edf2f7;
  color: #243041;
  font-size: 0.86rem;
  vertical-align: middle;
}

.records-table tbody tr:hover {
  background: #f7fbfb;
}

.grid-name-cell strong {
  color: #0f172a;
  font-weight: 900;
}

.description-cell span {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.sort-command {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.sort-command .material-symbols-outlined {
  font-size: 18px;
}

.table-link {
  color: #0b5b70;
  font-weight: 900;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.status-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
}

.status-pill,
.tomo-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 900;
}

.status-pill {
  background: #edf7f5;
  color: #0b5b70;
}

.tomo-pill {
  background: #fff4d8;
  color: #75520b;
}

.muted-cell {
  color: #94a3b8;
  font-weight: 700;
}

.go-command {
  padding: 0 0.65rem;
  background: #e8f4f2;
  color: #0b5b70;
}

.go-command:hover,
.go-command:focus-visible {
  background: #d3ece8;
}

@media (max-width: 760px) {
  .grid-records-panel {
    border-right: 0;
    border-left: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .records-header {
    align-items: stretch;
    flex-direction: column;
    padding: 0.85rem 0.9rem;
  }

  .records-actions {
    justify-content: flex-start;
  }

  .records-state {
    min-height: 140px;
  }
}
</style>
